import { DataTypeProvider, DataTypeProviderProps } from '@devexpress/dx-react-grid';
import { FileCopy, Warning as WarningIcon } from '@mui/icons-material';
import { Chip, Tooltip, Typography } from '@mui/material';
import { Theme, TypographyProps } from '@mui/material';
import { green, red } from '@mui/material/colors';
import * as Schema from 'generated/graphql/schema';
import * as i18next from 'i18next';
import { startCase } from 'lodash';
import * as React from 'react';

import EditButton from '@/components/buttons/edit-button';
import { formatTargetPerformanceDirect } from '@/helpers/format-target';
import { timeFormatyyyyMMdhmmss } from '@/helpers/helper-functions';
import { WithUtils } from '@/hocs';
import { stopTypeNames } from '@/lib/stop-type-names';
import * as Types from '@/types/types';

import { MutationIconButton } from './buttons';

/**
 * Timestamp to date formatting.
 */
interface OptionsProps {
  locale?: string;
  options?: Intl.DateTimeFormatOptions;
}
export const TimestampTypeProvider = (props: DataTypeProviderProps & { format?: OptionsProps }) => (
  <DataTypeProvider formatterComponent={TimestampFormatter(props.format)} {...props} />
);

export const TimestampFormatter =
  (format?: OptionsProps) =>
  ({ value }: { value: DataTypeProvider.ValueFormatterProps['value'] }) => {
    const timestamp = Date.parse(value);
    if (isNaN(timestamp) === false) {
      return timeFormatyyyyMMdhmmss(new Date(value), format?.locale);
    } else {
      return value;
    }
  };

export const reshapeFormat = ({ locale, options }: OptionsProps): OptionsProps => {
  switch (locale) {
    case 'da': {
      return { locale: 'en-GB', options };
    }
    case 'de': {
      return { locale: 'de-DE', options };
    }
    case 'fr': {
      return { locale: 'fr-FR', options };
    }
    default: {
      return { locale, options };
    }
  }
};

export const DateFormatter = (date: Date, i18nLocal?: string, i18nOptions?: Intl.DateTimeFormatOptions) => {
  const { locale, options } = reshapeFormat({ locale: i18nLocal, options: i18nOptions });
  return date.toLocaleString(locale, options);
};

/**
 * User status formatting.
 */
const StartCaseFormatter = ({ row, value }: DataTypeProvider.ValueFormatterProps) => {
  const output = row.enabled ? startCase(value) : `DISABLED / ${startCase(value)}`;

  return <span>{output}</span>;
};

export const StartCaseProvider = (
  props: DataTypeProviderProps & {
    [x: string]: unknown;
  },
) => <DataTypeProvider formatterComponent={StartCaseFormatter} {...props} />;

/**
 * Stop cause formatting.
 */
export const StopsCauseTypeFormatter =
  (t: i18next.TFunction, theme: Theme, defaultValue?: string) =>
  ({ value, row }: DataTypeProvider.ValueFormatterProps) => {
    if ((!value || value === '') && defaultValue) {
      value = defaultValue;
    }
    let colorLookup = value;
    if (!stopTypeNames(t)[value as keyof typeof stopTypeNames] && row.type) {
      colorLookup = row.type;
    } else if (stopTypeNames(t)[value as keyof typeof stopTypeNames]) {
      value = stopTypeNames(t)[value as keyof typeof stopTypeNames];
    }
    let backgroundColor = theme.blackbird.stops.UNREGISTERED.color.main;
    if (colorLookup in theme.blackbird.stops) {
      backgroundColor = theme.blackbird.stops[colorLookup as keyof typeof theme.blackbird.stops].color.main;
    }
    return <Chip label={value} style={{ backgroundColor }} />;
  };

export const StopsCauseTypeProvider = (
  props: DataTypeProviderProps & {
    t: i18next.TFunction;
    theme: Theme;
    defaultValue?: string;
    [x: string]: unknown;
  },
) => (
  <DataTypeProvider formatterComponent={StopsCauseTypeFormatter(props.t, props.theme, props.defaultValue)} {...props} />
);

/**
 * Role chip formatting.
 */
const RoleTypeFormatter = ({ value }: DataTypeProvider.ValueFormatterProps) => {
  if (value) {
    return (
      <span>
        {value.name}{' '}
        <Chip
          style={{
            fontSize: '9px',
            height: '22px',
          }}
          color="primary"
          variant="outlined"
          label={value.type}
        />
      </span>
    );
  }
  return <div />;
};

export const RoleTypeProvider = (
  props: DataTypeProviderProps & {
    [x: string]: unknown;
  },
) => <DataTypeProvider formatterComponent={RoleTypeFormatter} {...props} />;

/**
 * Duration formatting.
 */
export const DurationFormatter =
  ({
    utils,
    t,
    exactTime = false,
    warnWhenNegative = false,
    variant = 'body2',
    skipJsx = false,
  }: {
    utils: WithUtils['utils'];
    t: i18next.TFunction;
    exactTime?: boolean;
    warnWhenNegative?: boolean;
    variant?: TypographyProps['variant'];
    skipJsx?: boolean;
  }) =>
  ({
    value,
    strict = false,
  }: Pick<DataTypeProvider.ValueFormatterProps, 'value'> & {
    strict?: boolean;
  }) => {
    let textValue;
    if (value === 0) {
      // FIXME: Needs translation.
      textValue = t(['shared:0Seconds'], { defaultValue: '0 seconds' });
    } else {
      if (typeof value === 'number') {
        if (strict) {
          textValue = utils.formatDistanceStrict(0, value);
        } else {
          textValue = utils.formatDistance(0, value, { includeSeconds: true });
        }
        if (value < 0) {
          textValue = t(['shared:nTimeGained'], { defaultValue: '{{time}} gained', time: textValue });
        }
      } else {
        textValue = value;
      }
    }
    // Note: Format this way, instead of using date-fns, because date-fns format is timezone aware,
    // resulting in format(0, 'HH:mm:ss') = '01:00:00' for GMT+1.
    const toHHMMSS = (v: number) => {
      const secNum = Math.abs(Math.floor(v / 1000));
      let hours: number | string = Math.floor(secNum / 3600);
      let minutes: number | string = Math.floor((secNum - hours * 3600) / 60);
      let seconds: number | string = secNum - hours * 3600 - minutes * 60;

      hours = hours === 0 || isNaN(hours) ? '' : `${hours}` + t(['shared:abbrHour'], { defaultValue: 'h' });
      minutes =
        minutes === 0 || isNaN(minutes) ? '' : `${minutes}` + t(['shared:abbrMinutes'], { defaultValue: 'min' });
      seconds =
        seconds === 0 || isNaN(seconds) ? '' : `${seconds}` + t(['shared:abbrSeconds'], { defaultValue: 'sec' });

      const fs = v === 0 ? t(['shared:nothing'], { defaultValue: 'Nothing' }) : hours + ' ' + minutes + ' ' + seconds;

      return v < 0 ? `-${fs}` : `${fs}`;
    };

    if (skipJsx) {
      return exactTime ? toHHMMSS(value) : textValue;
    }

    return (
      <div style={{ display: 'inline-block' }}>
        <Tooltip title={exactTime ? textValue : toHHMMSS(value)} placement={'top'}>
          <Typography variant={variant}>
            {exactTime ? toHHMMSS(value) : textValue}{' '}
            {warnWhenNegative && value < 0 ? (
              <WarningIcon color="secondary" style={{ marginLeft: '8px', fontSize: '20px', marginBottom: '-4px' }} />
            ) : null}
          </Typography>
        </Tooltip>
      </div>
    );
  };

export const AheadBehindTypeProvider = (
  props: DataTypeProviderProps & {
    t: i18next.TFunction;
    [x: string]: unknown;
  },
) => (
  <DataTypeProvider
    formatterComponent={({ value }) => (
      <span style={{ color: value === 0 ? undefined : value > 0 ? green[500] : red[500] }}>
        {formatTargetPerformanceDirect(props.t, value)}
      </span>
    )}
    {...props}
  />
);

export const DurationTypeProvider = (
  props: DataTypeProviderProps & {
    utils: WithUtils['utils'];
    t: i18next.TFunction;
    warnWhenNegative?: boolean;
    [x: string]: unknown;
  },
) => (
  <DataTypeProvider
    formatterComponent={DurationFormatter({
      utils: props.utils,
      t: props.t,
      exactTime: true,
      warnWhenNegative: props.warnWhenNegative,
    })}
    {...props}
  />
);

/**
 * Alarm type formatting.
 */
export const AlarmTypeTypeProvider = (
  props: DataTypeProviderProps & {
    t: i18next.TFunction;
    [x: string]: unknown;
  },
) => <DataTypeProvider formatterComponent={AlarmTypeFormatter(props.t)} {...props} />;
export const AlarmTypeFormatter =
  (t: i18next.TFunction) =>
  ({ value }: DataTypeProvider.ValueFormatterProps) =>
    <span>{formatAlarmType(t, value)}</span>;

export const formatAlarmType = (t: i18next.TFunction, type: Schema.Alarm['type']) => {
  switch (type) {
    case Schema.AlarmType.AboveX:
      return t(['alarms:AboveX'], { defaultValue: 'value > x' });
    case Schema.AlarmType.BelowX:
      return t(['alarms:BelowX'], { defaultValue: 'value < x' });
    case Schema.AlarmType.AboveXForTSeconds:
      return t(['alarms:AboveXForTSeconds'], { defaultValue: 'value > x for t seconds' });
    case Schema.AlarmType.BelowXForTSeconds:
      return t(['alarms:BelowXForTSeconds'], { defaultValue: 'value < x for t seconds' });
    case Schema.AlarmType.AboveXForTSecondsWithinYTimeNTimes:
      return t(['alarms:AboveXForTSecondsWithinYTimeNTimes'], {
        defaultValue: 'value > x for t seconds, n times, within y seconds',
      });
    case Schema.AlarmType.BelowXForTSecondsWithinYTimeNTimes:
      return t(['alarms:BelowXForTSecondsWithinYTimeNTimes'], {
        defaultValue: 'value < x for t seconds, n times, within y seconds',
      });
    case Schema.AlarmType.SumOfTAboveXWithinYTime:
      return t(['alarms:SumOfTAboveXWithinYTime'], {
        defaultValue: 'SUM(time value > x) > t seconds, within y seconds',
      });
    case Schema.AlarmType.SumOfTBelowXWithinYTime:
      return t(['alarms:SumOfTBelowXWithinYTime'], {
        defaultValue: 'SUM(time value < x) > t seconds, within y seconds',
      });
    case Schema.AlarmType.StopForTSeconds:
      // Internally t is in seconds, but we display it in minutes - hence "StopForTSeconds" maps to "StopForTMinutes"!
      return t(['alarms:StopForTMinutes'], {
        defaultValue: 'Stop time > t minutes',
      });
    default:
      return Types.assertUnreachable(type);
  }
};

export const formatAlarmTypeInterpolated = (
  t: i18next.TFunction,
  type: Schema.Alarm['type'],
  config: Schema.Alarm['alarmConfiguration'],
) => {
  switch (type) {
    case Schema.AlarmType.AboveX:
      return t(['alarms:AboveXInterpolated'], { defaultValue: 'value > {{x}}', x: config.x });
    case Schema.AlarmType.BelowX:
      return t(['alarms:BelowXInterpolated'], { defaultValue: 'value < {{x}}', x: config.x });
    case Schema.AlarmType.AboveXForTSeconds:
      return t(['alarms:AboveXForTSecondsInterpolated'], {
        defaultValue: 'value > {{x}} for {{t}} seconds',
        x: config.x,
        t: config.t,
      });
    case Schema.AlarmType.BelowXForTSeconds:
      return t(['alarms:BelowXForTSecondsInterpolated'], {
        defaultValue: 'value < {{x}} for {{t}} seconds',
        x: config.x,
        t: config.t,
      });
    case Schema.AlarmType.AboveXForTSecondsWithinYTimeNTimes:
      return t(['alarms:AboveXForTSecondsWithinYTimeNTimesInterpolated'], {
        defaultValue: 'value > {{x}} for {{t}} seconds, {{n}} times, within {{y}} seconds',
        x: config.x,
        t: config.t,
        n: config.n,
        y: config.y,
      });
    case Schema.AlarmType.BelowXForTSecondsWithinYTimeNTimes:
      return t(['alarms:BelowXForTSecondsWithinYTimeNTimesInterpolated'], {
        defaultValue: 'value < {{x}} for {{t}} seconds, {{n}} times, within {{y}} seconds',
        x: config.x,
        t: config.t,
        n: config.n,
        y: config.y,
      });
    case Schema.AlarmType.SumOfTAboveXWithinYTime:
      return t(['alarms:SumOfTAboveXWithinYTimeInterpolated'], {
        defaultValue: 'SUM(time value > {{x}}) > {{t}} seconds, within {{y}} seconds',
        x: config.x,
        t: config.t,
        y: config.y,
      });
    case Schema.AlarmType.SumOfTBelowXWithinYTime:
      return t(['alarms:SumOfTBelowXWithinYTimeInterpolated'], {
        defaultValue: 'SUM(time value < {{x}}) > {{t}} seconds, within {{y}} seconds',
        x: config.x,
        t: config.t,
        y: config.y,
      });
    case Schema.AlarmType.StopForTSeconds:
      return t(['alarms:StopForTSecondsInterpolated'], {
        defaultValue: 'stop of type {{stopType}} > {{t}} seconds',
        stopType: config.stopType,
        t: config.t,
      });
    default:
      return Types.assertUnreachable(type);
  }
};

export const EditButtonProvider = (
  props: DataTypeProviderProps & {
    t: i18next.TFunction;
    [x: string]: unknown;
    id?: string;
  },
) => {
  const formatComponent = ({ value }: DataTypeProvider.ValueFormatterProps) => {
    return <EditButton id={props.id} item={null} t={props.t} onEdit={value} />;
  };
  return <DataTypeProvider formatterComponent={formatComponent} {...props} />;
};

export const ActionsProvider = (
  props: DataTypeProviderProps & {
    t: i18next.TFunction;
    theme: Theme;
    [x: string]: unknown;
    id?: string;
  },
) => {
  const { id, t, theme } = props;
  const formatComponent = ({ value }: DataTypeProvider.ValueFormatterProps) => {
    return (
      <>
        <EditButton id={id} item={null} t={t} onEdit={value.edit} style={{ padding: theme.spacing() }} />
        <MutationIconButton
          id={id}
          tooltipMsg={t(['shared:continue'], { defaultValue: 'Continue' })}
          onClick={value.continue}
          color="primary"
          style={{ padding: theme.spacing() }}
        >
          <FileCopy />
        </MutationIconButton>
      </>
    );
  };
  return <DataTypeProvider formatterComponent={formatComponent} {...props} />;
};
